/* eslint-disable import/no-unresolved */
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { isIOS } from 'react-device-detect';
import { OnboardingBodyLayout, PasswordValidationPopUp } from 'assets/components';
import { Tooltip } from 'react-tooltip';
import { RESET_PASSWORD } from '../mutations';
import { showAlert } from '../../../utilities/notifications';
import { validatePassword } from '../../../utilities/formValidation';
import { FLOWS, sendStats } from '../../../services/mixpanel';
import Input from '../../../components/Input';
import { clearFormError } from '../../../utilities/formsUtils';
import { revealPassword } from '../helpers';
import { encryptWithPublicKey, removeWhitespaces } from '../../../utilities/helpers';
import { MXP_RESET_PASSWORD_CONFIRM_NEW_PASSWORD } from '../mixpanel-events';
import { useSetting } from '../../../utilities/sharedHooks/settings';
import { PASSWORD_PUBLIC_KEY } from '../../../utilities/sharedQueries';

const CreateNewPassword = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();
  const { resetKey } = useParams();

  const [enablePasswordKMSEncryption] = useSetting(
    'app_features',
    'feature_toggle_enable_password_kms_encryption',
    false,
    false
  );

  const { data: publicKeyData } = useQuery(PASSWORD_PUBLIC_KEY, {
    enabled: !!enablePasswordKMSEncryption,
  });

  const [form, setForm] = useState({
    password: '',
    repPassword: '',
  });
  const [pswdValidation, setPswdValidation] = useState({
    isLongEnough: false,
    hasLowercaseLetter: false,
    hasUppercaseLetter: false,
    hasNumber: false,
    hasSpecialCharacter: false,
    hasNotAllowerSpecislChars: false,
  });
  const [showPopUp, setShowPopUp] = useState(false);

  const repeatPasswordElement = useRef(null);

  const { mutate: resetPassword, isLoading: isResettingPassword } = useMutation(RESET_PASSWORD, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        sendStats(MXP_RESET_PASSWORD_CONFIRM_NEW_PASSWORD, {}, FLOWS.PASSWORD_RESET);
        history.push('/onboarding/login', { resetPassword: true });
      }
    },
  });

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!validatePassword(form.password) || form.password !== form.repPassword) {
      return false;
    }

    try {
      if (enablePasswordKMSEncryption) {
        const encryptedPassword = await encryptWithPublicKey(
          publicKeyData.publicKey,
          form.password
        );
        resetPassword({
          resetKey,
          password: encryptedPassword,
          confirm_password: encryptedPassword,
        });
      } else {
        resetPassword({
          resetKey,
          password: form.password,
          confirm_password: form.repPassword,
        });
      }
    } catch (error) {
      return false;
    }

    return true;
  };

  const isContinueButtonDisabled = useMemo(
    () => !validatePassword(form.password) || form.password !== form.repPassword,
    [form.password, form.repPassword]
  );

  const handleBlur = (e) => {
    if (e && e?.target && e?.target?.value) {
      if (e.target.name === 'password') {
        if (!validatePassword(e.target.value)) {
          if (e.target.className.indexOf('has-error') === -1) {
            e.target.className += ' has-error';
          }
        } else if (form.repPassword && e.target.value !== form.repPassword) {
          if (repeatPasswordElement.current.className.indexOf('has-error') === -1) {
            repeatPasswordElement.current.className += ' has-error';
          }
        } else if (e.target.value === repeatPasswordElement.current.value) {
          repeatPasswordElement.current.className = repeatPasswordElement.current.className.replace(
            ' has-error',
            ''
          );
        } else {
          clearFormError(e);
          setShowPopUp(false);
        }
        setShowPopUp(false);
      } else if (e.target.name === 'repPassword') {
        if (form.password !== e.target.value) {
          if (e.target.className.indexOf('has-error') === -1) {
            e.target.className += ' has-error';
          }
        } else {
          clearFormError(e);
        }
      } else {
        clearFormError(e);
      }
    } else if (e?.target && e?.target?.name === 'password') {
      setShowPopUp(false);
    }
  };

  const handleChange = (e, fieldName) => {
    if (!e) return;
    if (!e.target) {
      setForm({ ...form, [fieldName]: removeWhitespaces(e) });
    } else {
      const { name, value } = e.target;
      setForm({ ...form, [name]: removeWhitespaces(value) });
      if (e.target.name === 'password') {
        setPswdValidation(validatePassword(removeWhitespaces(e.target.value), true));
      }
    }
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="form-password-reset"
          loading={isResettingPassword ? 'true' : 'false'}
          disabled={isContinueButtonDisabled}>
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="form-password-reset"
          loading={isResettingPassword ? 'true' : 'false'}
          disabled={isContinueButtonDisabled}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  return (
    <OnboardingBodyLayout
      renderButtons={renderButtons}
      title={translate('create_new_password')}
      withBack
      onPrevClick={history.goBack}>
      <form onSubmit={handleSubmitForm} id="form-password-reset">
        <Input
          className={`${isIOS ? 'ios' : ''}`}
          type="password"
          groupClassName={`mt-4 ${isIOS ? 'ios' : ''}`}
          placeholder={translate('new_password_input_label')}
          label={translate('new_password_input_label')}
          errorLabel={translate('password_input_error_label')}
          name="password"
          value={form.password}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={(e) => {
            clearFormError(e);
            setShowPopUp(true);
          }}
          required
          withFloatingLabel
          withErrorLabel
          inputGroupChildren={
            <div data-tooltip-id="reset-password-validation-tooltip">
              <span
                className="icon icon-open-icon h4 text-shadow"
                role="button"
                onClick={revealPassword}
                onKeyDown={null}
                tabIndex={-1}
                aria-label="Reveal password"
              />
              <div className="d-md-none">
                <PasswordValidationPopUp
                  showPopUp={showPopUp}
                  translate={translate}
                  pswdValidation={pswdValidation}
                />
              </div>
            </div>
          }
        />
        <Input
          type="password"
          className={`${isIOS ? 'ios' : ''}`}
          groupClassName={`mt-4 mb-0 ${isIOS ? 'ios' : ''}`}
          placeholder={translate('confirm_password_input_label')}
          label={translate('confirm_password_input_label')}
          errorLabel={translate('password_input_mismatch_error_label')}
          name="repPassword"
          value={form.repPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={(e) => {
            clearFormError(e);
          }}
          inputRef={repeatPasswordElement}
          required
          withFloatingLabel
          withErrorLabel
          inputGroupChildren={
            <>
              <span
                className="icon icon-open-icon h4 text-shadow"
                role="button"
                onClick={revealPassword}
                onKeyDown={null}
                tabIndex={-1}
                aria-label="Reveal password"
              />
            </>
          }
        />
      </form>
      <Tooltip
        id="reset-password-validation-tooltip"
        isOpen={showPopUp}
        place="right"
        variant="dark"
        opacity={1}
        className="d-none d-md-block p-3 rounded border-1 password-validation-tooltip">
        <PasswordValidationPopUp showPopUp translate={translate} pswdValidation={pswdValidation} />
      </Tooltip>
    </OnboardingBodyLayout>
  );
};

export default CreateNewPassword;
